import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getArtist, isFollowing, followArtist, unfollowArtist,
  type ArtistPublicProfile, type ArtistDate, type ArtistReliability,
} from '../../lib/artists';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../../auth/useAuth';
import { ReliabilityBadge } from '../../components/ReliabilityBadge';
import { formatDateShort } from '../../lib/format';
import './ArtistProfilePage.css';

/**
 * The public artist profile.
 *
 * FOLLOWING IS BUYER ONLY. The engine gates POST /follow/:id on requireRole('buyer'), so
 * the button appears only for a signed in buyer. Everyone else, including guests and
 * organizers, sees a prompt instead of a control that would answer 403. Rendering a
 * button that cannot work is worse than not rendering it.
 *
 * THE RELIABILITY BLOCK IS ABSENT, not empty, below the visibility threshold. The engine
 * omits it rather than sending zeroes, and this page branches on that rather than
 * rendering a panel full of nothing, which would read as an artist scoring zero on
 * everything.
 */

const PERCENT = (value: number) => `${Math.round(value * 100)}%`;

function DateList({ title, dates, empty }: { title: string; dates: ArtistDate[]; empty: string }) {
  return (
    <section className="ap-dates">
      <h2 className="ap-section-title">{title}</h2>
      {dates.length === 0 ? (
        <p className="ap-state">{empty}</p>
      ) : (
        <ul className="ap-date-list">
          {dates.map((d) => (
            <li key={d.eventId}>
              <Link to={`/events/${d.eventId}`} className="ap-date">
                <span className="ap-date__when mono">{formatDateShort(d.eventDate)}</span>
                <span className="ap-date__title">{d.title}</span>
                <span className="ap-date__where">
                  {[d.venueName, d.venueCity].filter(Boolean).join(', ')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReliabilityPanel({ reliability }: { reliability: ArtistReliability }) {
  const rows = [
    {
      label: 'Turned up',
      value: reliability.breakdown.showUpRate,
      weight: reliability.weights.showUp,
      hint: 'Events performed against events booked.',
    },
    {
      label: 'On time',
      value: reliability.breakdown.onTimeRate,
      weight: reliability.weights.onTime,
      hint: 'Performances that started on schedule.',
    },
    {
      label: 'Contract compliance',
      value: reliability.breakdown.contractComplianceRate,
      weight: reliability.weights.compliance,
      hint: 'Agreed terms met without dispute.',
    },
  ];

  return (
    <section className="ap-reliability">
      <h2 className="ap-section-title">Reliability</h2>
      <div className="ap-reliability__score">
        <span className="ap-reliability__number mono">{reliability.overallScore}</span>
        <span className="ap-reliability__outof">out of 100</span>
        <span className="ap-reliability__basis">
          from {reliability.eventsPerformed} performances across {reliability.eventsBooked} bookings
        </span>
      </div>

      <ul className="ap-reliability__rows">
        {rows.map((row) => (
          <li key={row.label} className="ap-metric">
            <div className="ap-metric__head">
              <span className="ap-metric__label">{row.label}</span>
              <span className="ap-metric__value mono">{PERCENT(row.value)}</span>
            </div>
            <div className="ap-metric__bar" aria-hidden="true">
              <span style={{ width: `${Math.min(100, Math.max(0, row.value * 100))}%` }} />
            </div>
            <p className="ap-metric__hint">
              {row.hint} Worth {Math.round(row.weight * 100)}% of the score.
            </p>
          </li>
        ))}
      </ul>

      <p className="ap-reliability__note">
        The score also reflects how an artist performs commercially and how organizers rate
        them. Those figures stay private to the artist, so only the conduct measures are
        shown here.
      </p>
    </section>
  );
}

export function ArtistProfilePage() {
  const { artistId } = useParams<{ artistId: string }>();
  const { user, isAuthenticated } = useAuth();

  const [artist, setArtist] = useState<ArtistPublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [following, setFollowing] = useState<boolean | null>(null);
  const [followBusy, setFollowBusy] = useState(false);

  const isBuyer = isAuthenticated && user?.role === 'buyer';

  useEffect(() => {
    if (!artistId) return;
    let cancelled = false;
    setLoading(true);
    getArtist(artistId)
      .then((result) => {
        if (cancelled) return;
        setArtist(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
        else setError(err instanceof ApiError ? err.message : 'Could not load this artist.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [artistId]);

  useEffect(() => {
    // Only a buyer can follow, and only a buyer may call /following. Asking as anyone
    // else would earn a 403 for no reason.
    if (!artistId || !isBuyer) return;
    let cancelled = false;
    isFollowing(artistId)
      .then((r) => !cancelled && setFollowing(r))
      .catch(() => !cancelled && setFollowing(false));
    return () => { cancelled = true; };
  }, [artistId, isBuyer]);

  async function toggleFollow() {
    if (!artistId || following == null) return;
    setFollowBusy(true);
    try {
      if (following) await unfollowArtist(artistId);
      else await followArtist(artistId);
      setFollowing(!following);
      // Keep the visible count honest without refetching the whole profile.
      setArtist((a) => (a ? { ...a, followers: a.followers + (following ? -1 : 1) } : a));
    } catch {
      setError('Could not update your follow. Please try again.');
    } finally {
      setFollowBusy(false);
    }
  }

  if (loading) return <p className="ap-state">Loading artist…</p>;

  if (notFound) {
    return (
      <div className="ap-missing">
        <h1>Artist not found</h1>
        <p>This profile is not available. It may not be approved yet.</p>
        <Link to="/artists" className="button button--primary">Browse artists</Link>
      </div>
    );
  }

  if (error && !artist) {
    return <div className="alert alert--error" role="alert">{error}</div>;
  }
  if (!artist) return null;

  const meta = [artist.genre, artist.primaryMarket].filter(Boolean).join(' | ');
  const social = artist.social;

  return (
    <div className="artist-profile">
      <nav className="ap-crumbs">
        <Link to="/artists">← All artists</Link>
      </nav>

      <header className="ap-hero">
        <div className="ap-hero__avatar">
          {artist.profileImageUrl ? (
            <img src={artist.profileImageUrl} alt="" />
          ) : (
            <span className="display" aria-hidden="true">{artist.stageName.slice(0, 1)}</span>
          )}
        </div>

        <div className="ap-hero__body">
          <h1 className="ap-hero__name display">{artist.stageName}</h1>
          {meta && <p className="ap-hero__meta mono">{meta}</p>}
          <div className="ap-hero__badges">
            <ReliabilityBadge
              score={artist.reliabilityScore}
              buildingTrackRecord={artist.buildingTrackRecord}
              size="md"
            />
            <span className="ap-hero__followers mono">
              {artist.followers.toLocaleString()} following
            </span>
          </div>

          {isBuyer ? (
            <button
              type="button"
              className={following ? 'button button--ghost' : 'button button--primary'}
              onClick={() => void toggleFollow()}
              disabled={followBusy || following == null}
            >
              {following == null
                ? 'Checking…'
                : followBusy
                  ? 'Saving…'
                  : following ? 'Following' : 'Follow'}
            </button>
          ) : (
            <p className="ap-hero__follow-note">
              {isAuthenticated
                ? 'Following is available on ticket buyer accounts.'
                : 'Sign in as a ticket buyer to follow this artist and hear about new dates.'}
            </p>
          )}
        </div>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {artist.bio && (
        <section className="ap-bio">
          <h2 className="ap-section-title">About</h2>
          <p>{artist.bio}</p>
        </section>
      )}

      <div className="ap-columns">
        <div className="ap-main">
          <DateList
            title="Upcoming dates"
            dates={artist.upcomingDates}
            empty="No confirmed dates coming up."
          />
          <DateList
            title="Past dates"
            dates={artist.pastDates}
            empty="No past performances on Voleto yet."
          />
        </div>

        <aside className="ap-aside">
          {artist.reliability ? (
            <ReliabilityPanel reliability={artist.reliability} />
          ) : (
            <section className="ap-reliability ap-reliability--new">
              <h2 className="ap-section-title">Reliability</h2>
              <p className="ap-state">
                This artist is building a track record. A score appears once they have
                completed {artist.visibilityThreshold} events on Voleto.
              </p>
            </section>
          )}

          {(social.instagram.handle || social.tiktok.handle
            || social.spotifyMonthlyListeners || social.youtubeSubscribers) && (
            <section className="ap-social">
              <h2 className="ap-section-title">Audience</h2>
              <dl className="ap-social__list">
                {social.instagram.handle && (
                  <div>
                    <dt>Instagram</dt>
                    <dd className="mono">
                      @{social.instagram.handle}
                      {social.instagram.followers
                        ? ` | ${social.instagram.followers.toLocaleString()}`
                        : ''}
                    </dd>
                  </div>
                )}
                {social.tiktok.handle && (
                  <div>
                    <dt>TikTok</dt>
                    <dd className="mono">
                      @{social.tiktok.handle}
                      {social.tiktok.followers
                        ? ` | ${social.tiktok.followers.toLocaleString()}`
                        : ''}
                    </dd>
                  </div>
                )}
                {social.spotifyMonthlyListeners != null && (
                  <div>
                    <dt>Spotify monthly listeners</dt>
                    <dd className="mono">{social.spotifyMonthlyListeners.toLocaleString()}</dd>
                  </div>
                )}
                {social.youtubeSubscribers != null && (
                  <div>
                    <dt>YouTube subscribers</dt>
                    <dd className="mono">{social.youtubeSubscribers.toLocaleString()}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
